import { Modal, useToast } from '@afterpaytouch/core'
import { PostVerifyMobileResponseBody } from '@afterpaytouch/portal-api/consumer/account/change-email/types'
import { useTranslation } from 'next-i18next'
import React, { createContext, Dispatch, FunctionComponent, SetStateAction, useCallback, useContext, useEffect, useState } from 'react'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'
import { useChangeEmailVerifyMobileMutation } from '../../state/account/changeEmail'
import { Confirm, Edit, Init } from './steps'

const I18N_NAMESPACE = ['common', 'account']

export const noop = (): void => {}

export const EditEmailAddressSteps = {
  Init: 'Init',
  Edit: 'Edit',
  Confirm: 'Confirm',
} as const
export type EditEmailAddressStep = (typeof EditEmailAddressSteps)[keyof typeof EditEmailAddressSteps]

export interface EditEmailAddressModalContextProps {
  step: EditEmailAddressStep
  setStep: Dispatch<SetStateAction<EditEmailAddressStep>>
  token: string
  setToken: Dispatch<SetStateAction<string>>
  newEmail: string
  setNewEmail: Dispatch<SetStateAction<string>>
  resetState: () => void
}

export const editEmailAddressModalContextDefaults: EditEmailAddressModalContextProps = {
  step: EditEmailAddressSteps.Init,
  setStep: noop,
  token: '',
  setToken: noop,
  newEmail: '',
  setNewEmail: noop,
  resetState: noop,
}

export const EditEmailAddressModalContext = createContext<EditEmailAddressModalContextProps>(editEmailAddressModalContextDefaults)

export const useEditEmailAddressModalContext = (): EditEmailAddressModalContextProps => {
  return { ...useContext(EditEmailAddressModalContext) }
}

export interface EditEmailAddressModalProviderProps {
  children: React.ReactNode
}

export const EditEmailAddressModalProvider: FunctionComponent<EditEmailAddressModalProviderProps> = ({ children }) => {
  const initialStep = EditEmailAddressSteps.Init
  const [step, setStep] = useState<EditEmailAddressStep>(initialStep)
  const [token, setToken] = useState<string>('')
  const [newEmail, setNewEmail] = useState<string>('')
  const resetState = useCallback((): void => {
    setStep(initialStep)
    setToken('')
  }, [initialStep])

  const value = {
    step,
    setStep,
    token,
    setToken,
    newEmail,
    setNewEmail,
    resetState,
  }

  return <EditEmailAddressModalContext.Provider value={value}>{children}</EditEmailAddressModalContext.Provider>
}

interface EditEmailAddressModalProps {
  isModalOpen: boolean
  closeModal: () => void
}

export const EditEmailAddressModalSteps: FunctionComponent<EditEmailAddressModalProps> = ({ isModalOpen, closeModal }) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const setToast = useToast()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const { step, setToken, resetState } = useContext(EditEmailAddressModalContext)
  const renderStep = useCallback((): React.ReactNode => {
    switch (step) {
      case EditEmailAddressSteps.Init:
        return <Init />
      case EditEmailAddressSteps.Edit:
        return <Edit />
      case EditEmailAddressSteps.Confirm:
        return <Confirm closeModal={closeModal} />
    }
  }, [closeModal, step])

  useEffect(() => {
    if (!isModalOpen) {
      resetState()
    }
  }, [isModalOpen, resetState])

  const [verifyMobile, { data: verifyMobileData, isError: isVerifyMobileError, isLoading: isVerifyMobileLoading }] = useChangeEmailVerifyMobileMutation()

  const requestMobileCode = useCallback(async (): Promise<void> => {
    try {
      const res = (await verifyMobile()) as { data: PostVerifyMobileResponseBody }
      setToken(res.data.token)
    } catch (err) {
      closeModal()
      setToast({ message: t('account:edit:email:toast:error'), kind: 'Failure', manualDismiss: true, toastTime: 2000, testNameSpace })
    }
  }, [setToken, verifyMobile])

  useEffect(() => {
    requestMobileCode()
  }, [requestMobileCode])

  const onShow = (): void => {
    logEvent(TrackingEvent.CLICKED_EDIT_EMAIL)
  }

  const testNameSpace = 'edit-email-address-modal'

  return (
    <Modal
      show={isModalOpen}
      onShow={onShow}
      onClose={closeModal}
      onHide={closeModal}
      onBackdropClick={closeModal}
      onEscapeKeyDown={closeModal}
      loading={isVerifyMobileLoading}
      testNameSpace='edit-email-address'
    >
      {isVerifyMobileLoading && <div className='py-32'></div>}
      {/* {isVerifyMobileError && <div>Error</div>} */}
      {verifyMobileData != null && renderStep()}
    </Modal>
  )
}

export const EditEmailAddressModal: FunctionComponent<EditEmailAddressModalProps> = ({ isModalOpen, closeModal }) => {
  return isModalOpen ? (
    <EditEmailAddressModalProvider>
      <EditEmailAddressModalSteps isModalOpen={isModalOpen} closeModal={closeModal} />
    </EditEmailAddressModalProvider>
  ) : null
}
